import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const fallbackArticle = articles[0];
  const article = articles.find(a => a.slug === slug) || fallbackArticle;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-article-line text-foreground-400 text-2xl" />
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm font-label mb-4">该文章可能已被删除或不存在</p>
            <Link to="/" className="btn-primary text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter(a => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '51在线吃瓜网',
      logo: {
        '@type': 'ImageObject',
        url: `${'http://jnjiu.cn'}/vite.svg`,
      },
    },
    datePublished: article.publishedAt,
    dateModified: article.publishedAt,
    articleSection: article.category,
    keywords: article.tags.join(', '),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'http://jnjiu.cn/',
    },
  };

  const shareArticle = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt,
        url: 'http://jnjiu.cn/',
      });
    }
  };

  const renderContent = (text: string) => {
    return text.split('\n\n').map((paragraph, index) => (
      <p key={index} className="text-foreground-700 text-base md:text-lg leading-[1.8] mb-5 font-body">
        {paragraph}
      </p>
    ));
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <SEO
        title={`${article.title} - 51在线吃瓜网`}
        description={article.excerpt}
        keywords={`${article.title},${article.category},${article.tags.join(',')},51在线吃瓜`}
        canonicalUrl={`/article/${slug}`}
        ogType="article"
        ogImage={article.imageUrl}
        articlePublishedTime={article.publishedAt}
        articleModifiedTime={article.publishedAt}
      />
      <Navbar />
      <main className="pt-16">
        {/* Hero Image */}
        <div className="relative w-full h-[300px] md:h-[450px] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 max-w-[1440px] mx-auto px-4 md:px-6 pb-6 md:pb-10">
            <div className="flex items-center gap-2 mb-3">
              <span className="tag-pill bg-primary-500 text-white font-label">{article.category}</span>
              {article.isExclusive && (
                <span className="tag-pill bg-accent-500 text-white font-label">51独家</span>
              )}
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-black text-white leading-tight">
              {article.title}
            </h1>
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-8 md:py-12">
          {/* Breadcrumb + Meta */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-background-200/70">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex items-center gap-4 text-foreground-400 text-xs font-label">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {article.publishedAt}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line" />
                {(article.heat / 10000).toFixed(1)}w 热度
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Article Content */}
            <article className="lg:col-span-8">
              <div className="bg-white rounded-xl p-5 md:p-8 border border-background-200/70">
                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {article.tags.map(tag => (
                    <span key={tag} className="tag-pill bg-background-100 text-foreground-600 font-label">
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Content */}
                <div className="article-content">
                  {renderContent(article.content)}
                </div>

                {/* Share */}
                <div className="mt-8 pt-6 border-t border-background-200/70 flex items-center justify-between">
                  <span className="text-foreground-500 text-sm font-label">分享这篇爆料：</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={shareArticle}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer"
                      aria-label="分享"
                    >
                      <i className="ri-share-line text-sm" />
                    </button>
                    <a
                      href="http://jnjiu.cn/"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors"
                      aria-label="分享到微博"
                    >
                      <i className="ri-weibo-line text-sm" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <div className="mt-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-5">相关推荐</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {relatedArticles.map(related => (
                      <Link
                        key={related.id}
                        to={`/article/${related.slug}`}
                        className="group bg-white rounded-xl overflow-hidden card-hover border border-background-200/70"
                      >
                        <div className="aspect-[16/10] overflow-hidden">
                          <img
                            src={related.imageUrl}
                            alt={related.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="font-heading font-bold text-foreground-900 text-sm line-clamp-2 group-hover:text-primary-500 transition-colors">
                            {related.title}
                          </h3>
                          <div className="mt-2 flex items-center gap-3 text-foreground-400 text-xs font-label">
                            <span>{related.publishedAt}</span>
                            <span>{(related.heat / 10000).toFixed(1)}w</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-4 space-y-6">
              {/* Hot List */}
              <div className="bg-white rounded-xl p-5 border border-background-200/70">
                <h3 className="font-heading font-bold text-foreground-900 text-sm mb-4">全站热榜</h3>
                <div className="flex flex-col gap-3">
                  {articles
                    .filter(a => a.id !== article.id)
                    .sort((a, b) => b.heat - a.heat)
                    .slice(0, 6)
                    .map((a, i) => (
                      <Link
                        key={a.id}
                        to={`/article/${a.slug}`}
                        className="flex items-start gap-3 group"
                      >
                        <span className={`w-5 h-5 flex items-center justify-center rounded-md text-xs font-label font-bold flex-shrink-0 ${
                          i < 3 ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-500'
                        }`}>
                          {i + 1}
                        </span>
                        <p className="text-foreground-700 text-sm font-label line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {a.title}
                        </p>
                      </Link>
                    ))}
                </div>
              </div>

              {/* Categories */}
              <div className="bg-white rounded-xl p-5 border border-background-200/70">
                <h3 className="font-heading font-bold text-foreground-900 text-sm mb-4">热门分类</h3>
                <div className="flex flex-wrap gap-2">
                  {['网红黑料', '明星八卦', '每日吃瓜', '最新爆料', '吃瓜大赛', '综艺互撕'].map(tag => (
                    <span key={tag} className="tag-pill bg-background-50 text-foreground-600 font-label cursor-pointer hover:bg-primary-50 hover:text-primary-500 transition-colors">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}